import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineBillingPoint, DineBillSummary } from 'api/types';

/**
 * A manager moves an unpaid bill to another counter of the outlet, with a
 * reason (DO-313, BR-067). The move is logged; a cashier cannot do it.
 */
interface Props {
  isOpen: boolean;
  bill: DineBillSummary | null;
  onClose: () => void;
  onMoved: () => void;
}

const DineOpsMoveBillModal: React.FC<Props> = ({ isOpen, bill, onClose, onMoved }) => {
  const [counters, setCounters] = useState<DineBillingPoint[]>([]);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTarget(''); setReason(''); setErrors({}); setServerError(null);
    dineOpsService.getBillingPoints().then(setCounters).catch(() => setCounters([]));
  }, [isOpen]);

  if (!isOpen || !bill) return null;
  const choices = counters.filter((c) => c.isActive && c.id !== bill.billingPointId);
  const targetName = counters.find((c) => c.id === target)?.billingPointName ?? '';

  const ask = () => {
    const e: Record<string, string> = {};
    if (!target) e.target = 'Choose the counter.';
    if (!reason.trim()) e.reason = 'Say why the bill is moved.';
    setErrors(e);
    if (!Object.keys(e).length) setConfirmOpen(true);
  };

  const move = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setServerError(null);
    try {
      await dineOpsService.moveBill(bill.id, target, reason.trim());
      notificationService.toast.success(`${bill.billNumber} moved to ${targetName}.`);
      setSaving(false);
      onMoved();
      onClose();
    } catch (err: any) {
      setSaving(false);
      setServerError(err?.response?.data?.message ?? 'The bill could not be moved.');
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm" style={{ minHeight: 'auto', maxWidth: 480 }}>
        {saving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Moving...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Move {bill.billNumber}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <p style={{ marginTop: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            Now at <strong>{bill.billingPointName ?? '—'}</strong>. The bill moves to the other counter&rsquo;s queue; who moved it and why is recorded.
          </p>
          {serverError && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
          <div className="form-group" data-field="target">
            <SyutSelect label={<>Move to <span className="text-danger">*</span></>} options={choices.map((c) => ({ value: c.id, label: c.billingPointName }))}
              value={target} onChange={(v) => { setTarget((v as string) || ''); setErrors((p) => ({ ...p, target: '' })); }}
              placeholder={choices.length ? 'Choose a counter' : 'No other counter'} size="sm" error={errors.target || undefined} menuPortalTarget={document.body} />
          </div>
          <div className="form-group" data-field="reason">
            <label className="form-label" htmlFor="move-reason">Reason <span className="text-danger">*</span></label>
            <input id="move-reason" className={`form-input${errors.reason ? ' form-field-error' : ''}`} value={reason} maxLength={500}
              onChange={(e) => { setReason(e.target.value); setErrors((p) => ({ ...p, reason: '' })); }} placeholder="e.g., guest paying at Counter 1" />
            {errors.reason && <div className="form-error-message">{errors.reason}</div>}
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={ask} disabled={saving || choices.length === 0}>Move bill</button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen} title="Move bill"
        message={`Move ${bill.billNumber} from ${bill.billingPointName ?? 'its counter'} to ${targetName}?`}
        confirmLabel="Move" cancelLabel="Cancel" variant="default" onConfirm={() => void move()} onCancel={() => setConfirmOpen(false)} />
    </>
  );
};

export default DineOpsMoveBillModal;
